import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Users, UserPlus, Calendar, TrendingUp, Briefcase, BarChart3, Settings, Home, ChevronRight, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Sidebar = () => {
  const location = useLocation();
  const { signOut } = useAuth();

  const menuItems = [
    {
      category: 'Main',
      items: [
        { 
          name: 'Dashboard', 
          path: '/admin-home-dashboard', 
          icon: Home, 
          description: 'Overview & metrics' 
        },
      ]
    },
    {
      category: 'HR Management',
      items: [
        { 
          name: 'Employee Directory', 
          path: '/employee-directory', 
          icon: Users, 
          description: 'Manage team members' 
        },
        { 
          name: 'Employee Onboarding', 
          path: '/employee-onboarding', 
          icon: UserPlus, 
          description: 'New hire processes' 
        },
        { 
          name: 'Time-off Management', 
          path: '/time-off-management', 
          icon: Calendar, 
          description: 'Leave & vacation tracking' 
        },
        { 
          name: 'Performance Management', 
          path: '/performance-management', 
          icon: TrendingUp, 
          description: 'Reviews & goal tracking' 
        },
      ]
    },
    {
      category: 'ATS & Recruiting',
      items: [
        { 
          name: 'Job Postings', 
          path: '/app/jobs', 
          icon: Briefcase, 
          description: 'Manage open positions' 
        },
        { 
          name: 'Candidate Pipeline', 
          path: '/app/pipeline', 
          icon: Users, 
          description: 'Track applications' 
        },
        { 
          name: 'Analytics Dashboard', 
          path: '/app/analytics', 
          icon: BarChart3, 
          description: 'Hiring insights' 
        },
      ]
    },
    {
      category: 'Administration',
      items: [
        { 
          name: 'Admin Dashboard', 
          path: '/app/admin', 
          icon: Settings, 
          description: 'System administration' 
        },
      ]
    }
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const isActive = (path) => location?.pathname === path;

  return (
    <div className="w-64 bg-gray-900 text-white h-screen overflow-y-auto fixed left-0 top-0 z-30">
      {/* Header */}
      <div className="p-6 border-b border-gray-700">
        <Link to="/admin-home-dashboard" className="block">
          <h1 className="text-xl font-bold text-purple-400 hover:text-purple-300 transition-colors">
            Intime HR
          </h1>
          <p className="text-sm text-gray-400 mt-1">Complete HRIS Platform</p>
        </Link>
      </div>
      {/* Navigation */}
      <nav className="flex-1 p-4">
        {menuItems?.map((category, categoryIndex) => (
          <div key={categoryIndex} className="mb-8">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              {category?.category}
            </h3>
            <ul className="space-y-2">
              {category?.items?.map((item, itemIndex) => (
                <li key={itemIndex}>
                  <Link
                    to={item?.path}
                    className={`flex items-center p-3 rounded-lg transition-all duration-200 group ${
                      isActive(item?.path)
                        ? 'bg-purple-600 text-white shadow-lg'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <item.icon className={`h-5 w-5 mr-3 ${
                      isActive(item?.path) ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'
                    }`} />
                    <div className="flex-1">
                      <div className="font-medium">{item?.name}</div>
                      <div className={`text-xs ${
                        isActive(item?.path) ? 'text-purple-100' : 'text-gray-400 group-hover:text-gray-300'
                      }`}>
                        {item?.description}
                      </div>
                    </div>
                    <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${
                      isActive(item?.path) ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'
                    }`} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      {/* Footer */}
      <div className="p-4 border-t border-gray-700">
        <button
          onClick={handleSignOut}
          className="flex items-center w-full p-3 text-gray-300 hover:bg-gray-800 hover:text-white rounded-lg transition-colors duration-200"
        >
          <LogOut className="h-5 w-5 mr-3" />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;